import { configureStore } from '@reduxjs/toolkit'
import createSagaMiddleware from 'redux-saga'

import { rtkQueryErrorLogger } from './middlewareRtk'
import rootReducers from './rootReducers'

import { menuReservasApi } from '@/common/components/Layout/services/menuReservas.services'
import { usuarioActualApi } from '@/common/components/Layout/services/usuarioActual.services'
import { catalogosApi } from '@/common/services/catalogos.services'
import rootSaga from '@/core/store/rootSagas'
import { reportarError } from '@/core/telemetria/reportarError'
import { asignacionEjecutivosComercialesApi } from '@/modules/Administrator/AsignacionEjecutivosComerciales/services/asignacionEjecutivosComerciales.services'
import { configuracionReservasApi } from '@/modules/Administrator/ConfiguracionReservas/services/configuracionReservas.services'
import { historicoCambioReservaCentralizadaApi } from '@/modules/HistoricoCambioReservaCentralizada/services/historicoCambioReservaCentralizada.services'
import { dashboardOperativoDiaApi } from '@/modules/Home/components/DashboardOperativoDia/services/dashboardOperativoDia.services'
import { agenteOperativoApi } from '@/modules/Home/services/ejecutivoOperativo.services'
import { homeCountersApi } from '@/modules/Home/services/homeCounters.services'
import { operacionSemanalApi } from '@/modules/Home/services/operacionSemanal.services'
import { cartaReservaApi } from '@/modules/ReservasTemp/services/cartaReserva.services'
import { estadosLogicosApi } from '@/modules/ReservasTemp/services/estadosLogicos.services'
import { listasSeleccionApi } from '@/modules/ReservasTemp/services/listasSeleccion.services'
import { planPagoApi } from '@/modules/ReservasTemp/services/planPago.services'
import { reservaCentralizadaApi } from '@/modules/ReservasTemp/services/reservaCentralizada.services'
import { reservaCentralizadaBusinessApi } from '@/modules/ReservasTemp/services/reservaCentralizadaBusiness.services'
import { reservaCentralizadaDetalleApi } from '@/modules/ReservasTemp/services/reservaCentralizadaDetalle.services'
import { userConfigurationApi } from '@/modules/ReservasTemp/services/userConfiguration.services'
import { preferenciaMonedaApi } from '@/modules/Utility/services/preferenciaMoneda.services'

// config redux saga
// Sin onError un error no atrapado en una saga se perdía sin dejar rastro: no aparecía en consola
// y el usuario solo notaba que la app dejaba de responder. Se registra siempre para poder diagnosticar.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sagaMiddleware: any = createSagaMiddleware({
  onError: (error: Error, { sagaStack }: { sagaStack: string }): void => {
    reportarError(error, 'Error no atrapado en una saga de redux-saga', { sagaStack })
  },
})

// create store with configureStore
export const store = configureStore({
  reducer: rootReducers,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      // Solo-dev: el árbol de estado (caches RTK Query) es grande y el chequeo de inmutabilidad
      // se dispara >32ms en cada dispatch. Se desactiva en producción de todas formas.
      immutableCheck: false,
    }).concat([
      sagaMiddleware,
      rtkQueryErrorLogger,
      menuReservasApi.middleware,
      usuarioActualApi.middleware,
      estadosLogicosApi.middleware,
      reservaCentralizadaBusinessApi.middleware,
      reservaCentralizadaApi.middleware,
      reservaCentralizadaDetalleApi.middleware,
      listasSeleccionApi.middleware,
      planPagoApi.middleware,
      cartaReservaApi.middleware,
      homeCountersApi.middleware,
      operacionSemanalApi.middleware,
      dashboardOperativoDiaApi.middleware,
      agenteOperativoApi.middleware,
      userConfigurationApi.middleware,
      historicoCambioReservaCentralizadaApi.middleware,
      catalogosApi.middleware,
      asignacionEjecutivosComercialesApi.middleware,
      configuracionReservasApi.middleware,
      preferenciaMonedaApi.middleware,
    ]),
})

sagaMiddleware.run(rootSaga)

export type RootState = ReturnType<typeof store.getState>

export type AppDispatch = typeof store.dispatch
